'use client';
// Types + small subcomponents lifted from HoldingsList.tsx to keep the
// parent component under the 500-line god-file threshold.

import type { HoldingHistory, HoldingPnl, HoldingSelfPctile } from '@/lib/hooks/use-holdings-data';

export interface Holding {
  symbol: string;
  weight: number;
  price: number | null;
  composite: number | null;
  q: number | null;
  v: number | null;
  sector: string | null;
  history: HoldingHistory;
  pnl: HoldingPnl;
  /** Regime labels (qRegime/vRegime/cRegime) differentiate "no history"
   *  (count = 0) from "model-regime shift" (current value far outside the
   *  historical distribution; comparison no longer fair). */
  selfPctile: HoldingSelfPctile;
}

export interface TargetPriceRow {
  symbol: string;
  fiveYearTarget: number | null;
  threeYearTarget: number | null;
  asymmetry: number | null;
  upside: number | null;
  downside: number | null;
  currentPrice: number | null;
  consensusFairValue?: number | null;
  todayFairValue?: number | null;
  methodCoverage?: number | null;
  peerGroup?: string;
  marketCap?: number | null;
  fy1Eps?: number | null;
  targetMultiple?: object | null;
  evEbitdaMultiple?: number | null;
  evEbitdaFairValue?: number | null;
  fwdEbitdaRaw?: number | null;
  fwdNetDebtRaw?: number | null;
  dcfFairValue?: number | null;
  dcfBreakdown?: object | null;
  dcfDiscountRate?: number | null;
  costOfEquityBreakdown?: object | null;
  ownerEarningsPerShare?: number | null;
  pfcfMultiple?: number | null;
  fcfPerShare?: number | null;
  fcfRaw?: number | null;
  pfcfFairValue?: number | null;
}
export interface TargetPricesPayload { stocks?: TargetPriceRow[] }
export interface HedgeSignal { tier: 'HEDGE'; asymZ: number; asymmetry: number | null; mom12: number; firedAt: string }
export interface HedgeSignalsPayload { signals?: Record<string, HedgeSignal> }

export type SortKey =
  | 'weight'
  | 'composite'
  | 'fiveYrPct'
  | 'threeYrPct'
  | 'asym'
  | 'alphaDays'
  | 'day'
  | 'mtd'
  | 'ytd'
  | 'off52'
  | 'q'
  | 'qPct'
  | 'v'
  | 'vPct'
  | 'cPct'
  | 'hedge';
export type SortDir = 'asc' | 'desc';

/** "Micron Technology, Inc." → "Micron". Compact identifier next to ticker
 *  so the symbol cell self-narrates without giving the full name. */
export function firstWord(name: string | null | undefined): string {
  if (!name) return '';
  return name.replace(/[,.;]/g, '').trim().split(/\s+/)[0] ?? '';
}

export function SortHeader({
  sortKey,
  current,
  dir,
  onClick,
  color,
  align = 'right',
  title,
  children,
}: {
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onClick: (k: SortKey) => void;
  color?: string;
  align?: 'right' | 'center';
  title?: string;
  children: React.ReactNode;
}) {
  const isActive = current === sortKey;
  const arrow = isActive ? (dir === 'desc' ? '↓' : '↑') : '';
  const alignCls = align === 'center' ? 'text-center justify-center' : 'text-right justify-end';
  const baseColor = color ?? '';
  const activeColor = isActive ? 'text-amber-300' : baseColor;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      title={title}
      className={`col-span-1 ${alignCls} flex items-baseline gap-0.5 ${activeColor} hover:text-white transition-colors`}
    >
      <span>{children}</span>
      {arrow && <span className="text-[7px]">{arrow}</span>}
    </button>
  );
}
